// SimApi — a thin function-injection bus so other modules (like the editor)
// can ask the running SIM to restart or load a map without importing main.ts
// (which would create a circular dep).
//
// main.ts calls setSimApi() with real implementations during boot.
// Anyone else imports `simApi` and calls the methods. The game members are
// optional: a product without the game on top (explore) never sets them, and
// every caller of one handles its absence.

import type { WarpLevel } from '../world/clock';
import type { WarpGoal } from './warp';

/**
 * The warp level row, as the sim owns it.
 *
 * The Atlas draws its own row of buttons in the Solar System view and reads
 * and writes THIS, so there is one picked level and one clock rather than a
 * second copy of either. Nothing here says when a level may be reached or what
 * drops it: those rules live with the sim's own `setWarpPick` and `updateWarp`
 * and arrive through these functions.
 */
export interface SimWarpControl {
  /** The levels to draw, in the order the row draws them. */
  levels: () => readonly WarpLevel[];
  /** Which of them is picked: an index into `levels()`. */
  pick: () => number;
  /** What the readout says, which is not always a level's own label. */
  label: () => string;
  /** True while the row cannot be reached. */
  locked: () => boolean;
  /** The notice standing under the row, or null. */
  notice: () => string | null;
  /** Pick a level. The same call a click on the sim's own row makes. */
  setPick: (idx: number) => void;
  /**
   * Whether THIS level (an index into `levels()`) could be picked right now.
   * Asked per level rather than once for the whole row, so a rule that only
   * ever blocks some of them - the top level under thrust, say - needs no
   * second change here: today every index answers the same way, tomorrow's
   * rule can answer differently per index with nothing else moving.
   */
  reachable: (idx: number) => boolean;
  /** Why `reachable(idx)` is false right now, or null when it is true. A
   *  standing fact about the current hold, not the transient drop notice. */
  reason: (idx: number) => string | null;
  /** Warp toward a goal and stop there, on the same ladder and rules; the refusal, or null once it runs. */
  runTo: (goal: WarpGoal) => string | null;
  /** The run in hand, or null. */
  goal: () => WarpGoal | null;
}

/** Heliocentric ecliptic J2000 metres, held in doubles. */
export interface SimVec3 {
  x: number;
  y: number;
  z: number;
}

/** The player's own ship, as much of it as another view needs to draw a marker. */
export interface SimShipPlace {
  /** Where it is, or null before the sim has seeded a position. */
  at: () => SimVec3 | null;
  /**
   * How fast it is going in that same frame, metres a second, or null before
   * the sim has seeded one.
   *
   * The sim's own `flight.velocity` is relative to the body pulling hardest;
   * this is that with the frame parent's own velocity composed onto it, which
   * is the only heliocentric ship velocity there is. The delta-v map plans
   * from this rather than from an assumed circular orbit.
   */
  velocity: () => SimVec3 | null;
}

/**
 * The predicted-path line's on/off, as the pilot panel's TRAJECTORY button
 * owns it. The Atlas draws a second switch for the same state through this,
 * rather than a copy of its own.
 */
export interface SimTrajectoryControl {
  /** Whether the line is switched on right now. */
  enabled: () => boolean;
  /** Switch it. The same call the pilot panel's button makes. */
  setEnabled: (on: boolean) => void;
}

/**
 * The one save slot, as the menu reaches it. The sim owns what a save is and
 * what it refuses; this is the two buttons and the fact of a slot being full.
 */
export interface SimSaveControl {
  /** Capture the game as it stands and write it to the slot. */
  write: () => void;
  /** Put the slot back. A refusal goes to the comms log, not to the caller. */
  read: () => void;
  /** Whether there is anything in the slot to read. */
  has: () => boolean;
}

/** One hull the Ship Yard's picker offers. */
export interface SimShipChoice {
  /** The SHIP_SPECS key. */
  key: string;
  name: string;
  hullClass: string;
  /** An authored whole-hull mesh rather than one assembled from parts. */
  authored: boolean;
  radiatorPanelsPerSide: number;
}

/** The hull the player flies, as the Ship Yard's picker reaches it. */
export interface SimPlayerShipControl {
  /** What may be picked, authored hulls first. */
  choices: () => SimShipChoice[];
  /** The SHIP_SPECS key flown now. */
  current: () => string;
  /** Fly this hull. The refusal in words, or null once it is flown. */
  pick: (key: string) => string | null;
  /** Why no pick would be taken right now, or null. */
  blocked: () => string | null;
}

/** What every product's sim answers: the cruise the Atlas and the delta-v map ride on. */
export interface SimCruiseApi {
  /** Show the SIM (turns editor off if open). */
  showSim: () => void;
  /** The warp level row the sim owns and the Atlas draws a second face for. */
  warp: SimWarpControl;
  /** Where the ship is, which is what the Atlas's you-are-here marker follows. */
  ship: SimShipPlace;
  /** The trajectory line's switch, which the Atlas draws a second row for. */
  trajectory: SimTrajectoryControl;
  /** Why the map is holding the clock right now, or null while it runs. */
  clockHold: () => string | null;
}

/** What only a sim with the game on top answers. */
export interface SimGameApi {
  /** Reset all entity state to the current scenario's initial conditions. */
  restart: () => void;
  /** Replace the current scenario with the GameMap of the given id. */
  loadMap: (mapId: string) => void;
  /** Restore the original default scenario (Esperança Window canonical). */
  loadDefault: () => void;
  /** Leave the current fight and return to cruise. No-op in cruise. */
  leaveFight: () => void;
  /** True while a fight is mounted (the menu's Continue entry reads off this). */
  isFightOn: () => boolean;
  /** The save slot the menu's two buttons drive. */
  save: SimSaveControl;
  /** The Ship Yard's hull picker. */
  playerShip: SimPlayerShipControl;
}

export type SimApi = SimCruiseApi & Partial<SimGameApi>;

const noShip: SimShipPlace = {
  at: () => null,
  velocity: () => null,
};

const noTrajectory: SimTrajectoryControl = {
  enabled: () => false,
  setEnabled: () => console.warn('simApi.trajectory.setEnabled called before init'),
};

const noWarp: SimWarpControl = {
  levels: () => [],
  pick: () => 0,
  label: () => '',
  locked: () => true,
  notice: () => null,
  setPick: () => console.warn('simApi.warp.setPick called before init'),
  reachable: () => false,
  reason: () => null,
  runTo: () => 'NO CLOCK',
  goal: () => null,
};

const noop: SimCruiseApi = {
  showSim: () => console.warn('simApi.showSim called before init'),
  warp: noWarp,
  ship: noShip,
  trajectory: noTrajectory,
  clockHold: () => null,
};

export const simApi: SimApi = { ...noop };

export function setSimApi(impl: SimApi): void {
  simApi.showSim = impl.showSim;
  simApi.warp = impl.warp;
  simApi.ship = impl.ship;
  simApi.trajectory = impl.trajectory;
  simApi.clockHold = impl.clockHold;
  if (impl.restart) simApi.restart = impl.restart;
  if (impl.loadMap) simApi.loadMap = impl.loadMap;
  if (impl.loadDefault) simApi.loadDefault = impl.loadDefault;
  if (impl.leaveFight) simApi.leaveFight = impl.leaveFight;
  if (impl.isFightOn) simApi.isFightOn = impl.isFightOn;
  if (impl.save) simApi.save = impl.save;
  if (impl.playerShip) simApi.playerShip = impl.playerShip;
}
